import { ComponentPropsWithoutRef, forwardRef } from 'react';
import { FormError } from 'ui/components/elements/FormError/FormError';
import styled from '@emotion/styled';

type Props = ComponentPropsWithoutRef<'select'> & {
    options?: { value: string | number; label: string }[] | (string | number)[];
    error?: string;
    width?: string;
    position: 'first' | 'middle' | 'last';
    placeholder?: string;
};

const Wrapper = styled.span<{
    width?: string;
    position: 'first' | 'middle' | 'last';
}>`
    position: relative;
    display: inline-block;
    width: ${(props: { width?: string }) => props.width || '100%'};

    &::after {
        content: '';
        position: absolute;
        top: 14px;
        right: 14px;
        background: gray;
        height: calc(tan(60deg) * 8px / 2);
        width: 12px;
        clip-path: polygon(0 0, 100% 0, 50% 100%);
    }
`;

const Border = styled.span`
    position: relative;
    display: inline-block;
    width: 100%;

    ${(props: { position?: 'first' | 'middle' | 'last' }) =>
        props.position !== 'last' &&
        `
  &::before {
    content: '';
    position: absolute;
    top: 8px;
    right: -1px;
    height: calc(100% - 16px);
    width: 1px;
    background-color: #D9D9D9;
  }
  `}

    ${(props: { position?: 'first' | 'middle' | 'last' }) =>
        props.position !== 'first' &&
        `
  &::after {
    content: '';
    position: absolute;
    top: 8px;
    left: -1px;
    height: calc(100% - 16px);
    width: 1px;
    background-color: #D9D9D9;
  }
  `}
`;

const SelectElement = styled.select<{
    isSelected: boolean;
    position?: 'first' | 'middle' | 'last';
}>`
    width: 100%;
    position: relative;
    padding: 8px 12px;
    background-color: #fff;
    font-size: 14px;

    -webkit-appearance: none;
    appearance: none;

    border-radius: 8px;

    border-top-left-radius: ${(props: { position?: 'first' | 'middle' | 'last' }) =>
        props.position === 'first' ? '8px' : '0px'};
    border-bottom-left-radius: ${(props: { position?: 'first' | 'middle' | 'last' }) =>
        props.position === 'first' ? '8px' : '0px'};
    border-top-right-radius: ${(props: { position?: 'first' | 'middle' | 'last' }) =>
        props.position === 'last' ? '8px' : '0px'};
    border-bottom-right-radius: ${(props: { position?: 'first' | 'middle' | 'last' }) =>
        props.position === 'last' ? '8px' : '0px'};

    border: 1px solid var(--main-color-blue);

    border-left: ${(props: { position?: 'first' | 'middle' | 'last' }) =>
        props.position === 'first' ? '1px solid var(--main-color-blue)' : 'none'};
    border-right: ${(props: { position?: 'first' | 'middle' | 'last' }) =>
        props.position === 'last' ? '1px solid var(--main-color-blue)' : 'none'};
`;

const Error = styled.span`
    position: absolute;
    bottom: -12px;
    left: 10px;
    z-index: 2;
    white-space: nowrap;
`;

/**
 * フォーム セレクトボックス
 */
export const GroupedToggleFormSelect = forwardRef<HTMLSelectElement, Props>(({ ...props }, ref) => {
    const { id = '', name = '', value, options, placeholder, width, position, onChange } = props;
    return (
        <Wrapper width={width} position={position}>
            <Border position={position}>
                <SelectElement
                    id={id}
                    name={name}
                    aria-label={name}
                    value={value}
                    isSelected={value !== -1}
                    onChange={onChange}
                    ref={ref}
                    position={position}
                >
                    <option value={-1} disabled hidden>
                        {placeholder ?? '選択してください'}
                    </option>
                    {options?.map((option) =>
                        typeof option === 'string' || typeof option === 'number' ? (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ) : (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ),
                    )}
                </SelectElement>
            </Border>
            <Error>
                <FormError message={props.error} />
            </Error>
        </Wrapper>
    );
});

GroupedToggleFormSelect.displayName = 'GroupedToggleFormSelect';
